<template>
  <div class="project-map-root">   
    <l-map
      class="project-leaflet-map"
      ref="map"   
      @ready="onMapReady"
      @moveend="getMapBounds"
      :zoom="zoom" :center="center" :options="{zoomControl:false}"> 
      <l-control-zoom position="bottomleft"></l-control-zoom>
      <l-tile-layer
        v-for="tileProvider in tileProviders"
        :key="tileProvider.name"
        :name="tileProvider.name"
        :visible="tileProvider.visible"
        :url="tileProvider.url"
        :attribution="tileProvider.attribution"
        layer-type="base"
      />
      <v-marker-cluster>
        <l-marker
          v-for="circle in dataCircles"
          :key="circle.Title + circle.PNumber"
          :lat-lng="[circle.Lat, circle.Lon]"
          :icon="addIcon(circle)"
        >
          <l-popup
            :autoPanPadding="[40, 40]"
            :options="{ maxWidth: 760, minWidth: 520, autoPan: true }"
          >
            <div class="project-popup">
              <div class="popup-header">
                <h3>
                  {{ circle.Title }}
                  <a v-if="circle.PNumber" :href="circle.Link" target="_blank">({{ circle.PNumber }})</a>
                </h3>
                <div class="popup-subtitle">
                  {{ locationLabel(circle) }}<span v-if="circle.Year"> · {{ circle.Year }}</span>
                </div>
              </div>

              <div class="popup-section" v-if="descriptionText(circle)">
                <div class="section-label">Project Development Objective</div>
                <p class="text-preview">
                  {{ isObjectiveExpanded(circle) ? descriptionText(circle) : shortText(descriptionText(circle), 420) }}
                </p>
                <button
                  v-if="isLongText(descriptionText(circle), 420)"
                  class="text-toggle"
                  type="button"
                  @click.stop="toggleObjective(circle)"
                >
                  {{ isObjectiveExpanded(circle) ? 'Collapse objective' : 'See full objective' }}
                </button>
              </div>

              <div class="popup-info-grid">
                <div class="info-item">
                  <v-icon small>mdi-shape</v-icon>
                  <span class="info-label">Category</span>
                  <span>{{ circle.CategoryOfficial || 'N/A' }}</span>
                </div>
                <div class="info-item">
                  <v-icon small>mdi-train-car</v-icon>
                  <span class="info-label">Mode</span>
                  <span>{{ joinList(circle.Mode) }}</span>
                </div>
                <div class="info-item">
                  <v-icon small>mdi-lightbulb-on-outline</v-icon>
                  <span class="info-label">Knowledge Area</span>
                  <span>{{ joinList(circle.KeyTech) }}</span>
                </div>
                <div class="info-item">
                  <v-icon small>mdi-currency-usd</v-icon>
                  <span class="info-label">Investment</span>
                  <span>Tech: {{ circle.TechnologyRelated || 'N/A' }} · Total: {{ circle.TotalInvestment || 'N/A' }}</span>
                </div>
              </div>

              <div class="popup-media-grid" v-if="circle.StrikingPhotos || messageBullets(circle).length">
                <div class="popup-image-panel" v-if="circle.StrikingPhotos">
                  <v-img
                    class="popup-image"
                    title="Click to view in full screen"
                    :src="domain + '/static/images/projects/' + circle.StrikingPhotos"
                    @click="openImage(domain + '/static/images/projects/' + circle.StrikingPhotos)"
                  ></v-img>
                  <div class="image-caption" v-if="circle.PhotoDesc">{{ circle.PhotoDesc }}</div>
                </div>

                <div class="popup-section popup-messages" v-if="messageBullets(circle).length">
                  <div class="section-label">Key Takeaways</div>
                  <ul>
                    <li v-for="message in visibleMessages(circle)" :key="message">{{ message }}</li>
                  </ul>
                  <button
                    v-if="remainingMessages(circle).length"
                    class="text-toggle"
                    type="button"
                    @click.stop="toggleTakeaways(circle)"
                  >
                    {{ isTakeawaysExpanded(circle) ? 'Collapse takeaways' : 'See full takeaways' }}
                  </button>
                </div>
              </div>
            </div>
          </l-popup>
        </l-marker>
      </v-marker-cluster>



     <l-control position="bottomright" >
       <v-card
          max-width="200"
          class="mx-auto"
          outlined
        >         
          <v-list dense>
              <v-list-item  v-for="item in legends" :key="item.Mode">
                <v-list-item-icon style="height: 28px">
                  <v-img :src="item.src" max-height="30" max-width="30"></v-img>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title v-text="item.Mode"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
          </v-list>
        </v-card>
    </l-control>
    </l-map>
  </div>
</template>

<style>
@import "../assets/static/css/app.scss";
@import "../assets/static/css/MarkerCluster.css";

.leaflet-popup-content-wrapper {
  border-radius: 4px;
}

.leaflet-popup-content {
  margin: 0;
}

.project-map-root,
.project-leaflet-map {
  height: 100%;
  min-height: 100%;
  width: 100%;
}

.inactive-project-marker {
  background: transparent;
  border: 0;
}

.inactive-project-marker__badge {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #d9d9d9;
  border-radius: 50%;
  background: rgba(245, 245, 245, 0.9);
  box-shadow: 0 1px 4px rgba(80, 80, 80, 0.28);
  opacity: 0.78;
}

.inactive-project-marker__badge img {
  width: 32px;
  height: 32px;
}

.project-popup {
  width: min(720px, calc(100vw - 96px));
  max-height: none;
  padding: 18px 18px 16px;
  color: #1f2933;
  font-family: "Open Sans", Arial, sans-serif;
}

.popup-header {
  padding-right: 14px;
  margin-bottom: 12px;
}

.popup-header h3 {
  margin: 0 0 4px;
  font-size: 17px;
  line-height: 1.25;
  font-weight: 700;
}

.popup-subtitle {
  color: #667085;
  font-size: 13px;
}

.popup-section {
  margin-top: 10px;
}

.section-label {
  margin-bottom: 4px;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.text-preview {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
}

.popup-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 18px;
  margin-top: 14px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.info-item {
  display: grid;
  grid-template-columns: 20px 104px minmax(0, 1fr);
  align-items: start;
  gap: 6px;
  font-size: 13px;
  line-height: 1.35;
}

.info-label {
  color: #344054;
  font-weight: 700;
}

.popup-media-grid {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 16px;
  margin-top: 14px;
}

.popup-image {
  width: 100%;
  max-height: 150px;
  border-radius: 4px;
  cursor: pointer;
}

.image-caption {
  margin-top: 6px;
  color: #475467;
  font-size: 12px;
  font-style: italic;
  line-height: 1.35;
}

.popup-messages {
  margin-top: 0;
}

.popup-messages ul {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.42;
}

.text-toggle {
  display: inline-flex;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0b6fcf;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.text-toggle:hover {
  text-decoration: underline;
}

@media (max-width: 820px) {
  .project-popup {
    width: min(560px, calc(100vw - 72px));
  }

  .popup-info-grid,
  .popup-media-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<script>

import "leaflet";
import { divIcon, icon } from "leaflet";
import {
  LMap,
  LTileLayer,
  LControl,
  // LGeoJson,
  LMarker,
  LPopup,
  LControlZoom
} from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import axios from "axios";

// delete Icon.Default.prototype._getIconUrl;
// Icon.Default.mergeOptions({
//   iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
//   iconUrl: require("leaflet/dist/images/marker-icon.png"),
//   shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
// });

export default {
  name: "Mapview",
  props: ["update_filter", "selected_types", "selected_modes", "selected_techs", "selected_regions", "selected_statuses", "keywords_search"],
  components: {
    LMap,
    LTileLayer,
    // LControlLayers,
    // LGeoJson,
    LControl,
    LMarker,
    LPopup,
    LControlZoom,
    "v-marker-cluster": Vue2LeafletMarkerCluster,
  },
  data() {
    return {
      loading: false,
      zoom: 2,
      center: [0, 0],
      country_geojson: null,
      tileProviders: [
        {
          name: "OpenStreet",
          visible: true,
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          attribution:
            '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        },
      ],
      dataCircles: [],
      tooLong: true,
      icon: null,
      legends: [
        {"Mode":'Road', "src": this.$domain + "/static/images/Road.png"},
        {"Mode":'Transit', "src": this.$domain + "/static/images/Transit.png"},
        {"Mode":'Railway', "src": this.$domain + "/static/images/Railway.png"},
        {"Mode":'Maritime', "src": this.$domain + "/static/images/Maritime.png"},
        {"Mode": 'Aviation', "src": this.$domain + "/static/images/Aviation.png"},
      ],
      domain: this.$domain,
      mapBounds: null,
      expandedObjectives: {},
      expandedTakeaways: {},
    };
  },
  computed: {
    options() {
      return {
        onEachFeature: this.onEachFeatureFunction,
      };
    },
    optionsPoints() {
      return {
        onEachFeature: this.onEachPointFn,
      };
    },
    styleFunction() {
      return () => {
        return {
          weight: 1,
          color: "white",
          opacity: 0.8,
          fillColor: "blue",
          fillOpacity: 0.8,
        };
      };
    },
    onEachFeatureFunction() {
      return (feature, layer) => {
        const a = {
          name1: "value111",
          name2: 111111111,
        };
        var ht =
          '<div class="card">' +
          '<div class="card-body">' +
          '<h5 class="card-title">' +
          feature.properties.FORMAL_EN +
          "</h5>" +
          '<p class="card-text">' +
          "Some quick example text to build on the card title and make up the bulk of the" +
          "cards content." +
          "</p>" +
          '<ul class="list-group list-group-flush">';
        Object.entries(a).forEach(([k, v]) => {
          ht +=
            '<li class="list-group-item row">' +
            '<div class="col-2">' +
            k +
            ": </div>" +
            '<div class="col-10">' +
            v +
            "</div>" +
            "</li>";
        });
        ht +=
          '<li class="list-group-item row">' +
          '<div class="col-2">' +
          "Image: " +
          "</div>" +
          '<div class="col-10">' +
          '<img src="https://media.pixeltuner.de/wp-content/uploads/2018/06/aurora-1197755_640.jpg" alt="" style="max-width: 200px;">' +
          "</div>" +
          "</li>";

        ht += "</ul>" + "</div>" + "</div>";

        layer.bindPopup(ht, { permanent: false, sticky: true });

        layer.bindTooltip(feature.properties.NAME_EN, {
          permanent: false,
          sticky: false,
          direction: "center",
        });
      };
    },
    onEachPointFn() {
      return (feature, layer) => {
        console.log(feature, layer);    
      };
    },

  },
  methods: {
    openImage(img_url) {
      window.open(img_url, "_blank", "noopener")
    },
    zoomImg (img_url){
      this.selected_img = img_url

    },
    joinList(value) {
      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "N/A"
      }
      return value || "N/A"
    },
    locationLabel(project) {
      return [project.Region, project.Country, project.Place || project.City]
        .filter(Boolean)
        .join(", ")
    },
    descriptionText(project) {
      return project.ProjectDevelopmentObjective || project.Abstract || ""
    },
    shortText(text, limit) {
      if (!text || text.length <= limit) {
        return text
      }
      return `${text.slice(0, limit).trim()}...`
    },
    isLongText(text, limit) {
      return Boolean(text && text.length > limit)
    },
    projectKey(project) {
      return project._id || project.PNumber || project.Title
    },
    isObjectiveExpanded(project) {
      return Boolean(this.expandedObjectives[this.projectKey(project)])
    },
    toggleObjective(project) {
      const key = this.projectKey(project)
      this.$set(this.expandedObjectives, key, !this.expandedObjectives[key])
    },
    isTakeawaysExpanded(project) {
      return Boolean(this.expandedTakeaways[this.projectKey(project)])
    },
    toggleTakeaways(project) {
      const key = this.projectKey(project)
      this.$set(this.expandedTakeaways, key, !this.expandedTakeaways[key])
    },
    messageBullets(project) {
      const source = project.ProjectDescription || project.KeyTakeaways || ""
      return source
        .split(/\n+/)
        .map(item => item.replace(/^[-•*]\s*/, "").trim())
        .filter(Boolean)
    },
    firstMessages(project) {
      return this.messageBullets(project).slice(0, 3)
    },
    visibleMessages(project) {
      return this.isTakeawaysExpanded(project) ? this.messageBullets(project) : this.firstMessages(project)
    },
    remainingMessages(project) {
      return this.messageBullets(project).slice(3)
    },
    styleKeyNumbers(textStr){
      const nums = textStr.match(/\$*\d+(,*\d{3,})*(\.*\d+)*%*/gm)
      console.log(nums)
      if(!nums){
        return textStr
      }

      for(const n of nums){
        textStr = textStr.replaceAll(n, ("<span class='font-italic font-weight-bold'>"+ n +"</span>"))
      }
      return textStr
    },
    modeIconName(Mode) {
      if (Array.isArray(Mode)) {
        return Mode.length ? Mode[0] : "Road"
      }
      return Mode || "Road"
    },
    isInactiveProject(project) {
      return project.Active !== 1
    },
    addIcon(project){
      const m = this.modeIconName(project.Mode)
      const iconUrl = this.$domain + "/static/images/"+ m +".png"
      if (this.isInactiveProject(project)) {
        return divIcon({
          className: "inactive-project-marker",
          html: `<span class="inactive-project-marker__badge"><img src="${iconUrl}" alt="" /></span>`,
          iconSize: [40, 40],
          iconAnchor: [20, 43],
          popupAnchor: [0, -42]
        })
      }
      return icon({
      iconUrl,
      iconSize: [32, 32],
      iconAnchor: [16, 37],
      popupAnchor: [0, -40]
    })
    },
    generateColor(n) {
      if (n.length < 8) {
        n = n * 10 ** (8 - n.length);
      } else {
        n = n / 10 ** (n.length - 8);
      }
      return "#" + Math.floor(n / 5).toString(16);
    },
    encodeStr(text_str) {
      var result = "";
      for (var i = 0; i < text_str.length; i++) {
        var code = text_str.charCodeAt(i);
        result += code;
      }
      return result;
    },
    async getGeojson() {
      const response = await fetch(this.$domain + "/map/get_country_borders");
      // const response = await fetch('https://wb-engine.winbyai.com/map/get_country_borders');
      this.country_geojson = await response.json();
    },
    onMapReady() {
      this.$nextTick(() => {
        const map = this.$refs.map && this.$refs.map.mapObject
        if (map) {
          map.invalidateSize()
        }
        this.getMapBounds()
      })
    },
    async getProjectCoords() {
      console.log(this.selected_types)
      console.log(this.selected_techs)
      const path = this.$domain + "/map/get_project_list";
      axios.post(
        path,
        {
          'project_types': this.selected_types,
          'modes': this.selected_modes,
          'key_techs': this.selected_techs,
          'regions': this.selected_regions,
          'statuses': this.selected_statuses,
          'keywords_search': this.keywords_search,
        }
      ).then((res) => {
        console.log(res.data);
        this.dataCircles = res.data;
        this.$parent.project_cnt = res.data.length
      });
    },
    getMapBounds () {
      if (!this.$refs.map || !this.$refs.map.mapObject) {
        return
      }
      console.log(this.$refs.map)
      console.log(this.$refs.map.mapObject.getBounds())
      // this.mapBounds = this.$refs.map;
      // console.log(this.mapBounds)
    },

},
  async created() {
    // this.getGeojson()
    this.getProjectCoords();
  },
  watch: {
     update_filter(){
       console.log("updating filter")
      this.getProjectCoords()
    }
  },
};
</script>
